import React from 'react'

import Stack from '@mui/material/Stack'

import { useCategoryStore, useGlobalCopyStore } from '../../common'

import {
  generateMonthHeaders,
  getTrendsTableColumns,
  getTrendsTableRows,
} from '../utils/TrendsTable'

import { TrendsTable } from './trendstable/TrendsTable'

interface TrendsTableProps {
  sx?: any
  height?: number | string
  selectedDateRange: { start: Date; end: Date }
  selectedCategory?: string
  onClickRow?: (category: string) => void
}

const TrendsTableContainer: React.FC<TrendsTableProps> = ({
  selectedDateRange,
  selectedCategory,
  onClickRow,
  height = '100%',
  sx = {},
}) => {
  const { trends: copy } = useGlobalCopyStore()
  const { trendsCategories } = useCategoryStore()

  // Generate all headers for the selected date range
  const monthHeaders = React.useMemo(() => {
    return generateMonthHeaders(selectedDateRange.start, selectedDateRange.end)
  }, [selectedDateRange])

  const columns = React.useMemo(() => {
    return getTrendsTableColumns(monthHeaders, copy)
  }, [monthHeaders, copy])

  const parentCategory = React.useMemo(() => {
    if (selectedCategory) {
      return trendsCategories.find((category) => category.guid === selectedCategory)
    }
    return undefined
  }, [selectedCategory, trendsCategories])

  const [incomeRows, spendingRows] = React.useMemo(() => {
    let categories = trendsCategories
    if (parentCategory) {
      categories = parentCategory.subCategories.filter((c) =>
        c.monthlyAmounts.some((ma) => ma.amount !== 0),
      )
    }

    return [
      getTrendsTableRows(categories, copy, true, monthHeaders, parentCategory),
      getTrendsTableRows(categories, copy, false, monthHeaders, parentCategory),
    ]
  }, [trendsCategories, monthHeaders, parentCategory, copy])

  const totalSpendingRow = {
    id: 'total',
    category: copy.table_column_total,
    category_guid: 'total',
    isParent: false,
    top_level_category_guid: 'total',
    total: spendingRows.reduce((sum, row) => sum + row.total, 0),
    monthlyAmounts: spendingRows.reduce(
      (amounts, row) => {
        Object.entries(row.monthlyAmounts).forEach(([month, amount]) => {
          amounts[month] = (amounts[month] || 0) + amount
        })
        return amounts
      },
      {} as Record<string, number>,
    ),
  }

  return (
    <Stack
      gap={24}
      sx={{ height: height, width: '100%', overflowX: 'auto', boxShadow: 'none', ...sx }}
    >
      {(!parentCategory || (parentCategory && parentCategory.is_income)) && (
        <TrendsTable
          columns={columns}
          onRowClick={onClickRow}
          parentCategory={parentCategory}
          rows={incomeRows}
        />
      )}
      {(!parentCategory || (parentCategory && !parentCategory.is_income)) && (
        <TrendsTable
          columns={columns}
          onRowClick={onClickRow}
          parentCategory={parentCategory}
          rows={spendingRows}
          totalRow={totalSpendingRow}
        />
      )}
    </Stack>
  )
}

export default TrendsTableContainer
